import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getAccountTrades, syncAccountTrades, getAccountPerformanceStats } from '../api/api';
import SpecificTradeDetails from '../components/SpecificTradeDetails';
import AccountSelection from '../components/AccountSelection';
import PageLayout from '../components/PageLayout';
import PerformanceStats from '../components/PerformanceStats';
import EquityCurveWidget from '../components/dashboard-widgets/EquityCurveWidget';

const AccountTradeListPage = () => {
  const { account_uuid } = useParams();
  const navigate = useNavigate();
  const [trades, setTrades] = useState([]);
  const [totalCount, setTotalCount] = useState(0); // Total trades from pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20); // Backend default
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTradeId, setExpandedTradeId] = useState(null);
  
  // Backend-calculated performance stats
  const [performanceStats, setPerformanceStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Dummy states for AccountSelection props not directly used by this page in compact mode
  const [, setDummyBalance] = useState(null);
  const [, setDummyEquity] = useState(null);
  const dummySetRiskPercent = () => {};

  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'descending' });
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [activeDateFilter, setActiveDateFilter] = useState('last30Days'); // 'today', 'thisWeek', 'last30Days', or null
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);

  // Reset to page 1 when account changes
  useEffect(() => {
    setCurrentPage(1);
  }, [account_uuid]);

  // Fetch trades for current page with date filters
  useEffect(() => {
    if (account_uuid) {
      setIsLoading(true);
      setError(null); // Clear previous errors
      const params = { page: currentPage };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      getAccountTrades(account_uuid, params)
        .then((tradeData) => {
          // API returns paginated response: { count, next, previous, results }
          const tradesArray = tradeData?.results || tradeData;
          setTrades(Array.isArray(tradesArray) ? tradesArray : []);
          setTotalCount(tradeData?.count || (Array.isArray(tradesArray) ? tradesArray.length : 0));
          setHasNext(!!tradeData?.next);
          setHasPrevious(!!tradeData?.previous);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error(`Error fetching trades for account ${account_uuid}:`, err);
          setError(err.message || 'Failed to fetch data. Please try selecting another account.');
          setTrades([]); // Ensure trades is always an array even on error
          setIsLoading(false);
        });
    }
  }, [account_uuid, currentPage, startDate, endDate]);

  // Fetch performance stats from backend (calculated across ALL trades, not paginated)
  useEffect(() => {
    if (account_uuid) {
      setStatsLoading(true);
      getAccountPerformanceStats(account_uuid, startDate || null, endDate || null)
        .then((stats) => {
          setPerformanceStats(stats);
          setStatsLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching performance stats:', err);
          setPerformanceStats(null);
          setStatsLoading(false);
        });
    }
  }, [account_uuid, startDate, endDate]);

  const toggleTradeDetails = (tradeId) => {
    setExpandedTradeId(expandedTradeId === tradeId ? null : tradeId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getProfitLossColorClass = (value) => {
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return 'text-textSecondary';
    if (numericValue > 0) return 'text-success';
    if (numericValue < 0) return 'text-destructive';
    return 'text-textSecondary';
  };

  const sortedTrades = React.useMemo(() => {
    if (!Array.isArray(trades)) {
      console.warn('trades is not an array:', trades);
      return [];
    }
    let sortableTrades = [...trades];
    if (sortConfig.key) {
      sortableTrades.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'actual_profit_loss') {
          aValue = aValue === null ? -Infinity : parseFloat(aValue);
          bValue = bValue === null ? -Infinity : parseFloat(bValue);
        }
        if (sortConfig.key === 'created_at') {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }
        // Basic string sort for instrument (case-insensitive)
        if (sortConfig.key === 'instrument') {
          aValue = String(aValue).toLowerCase();
          bValue = String(bValue).toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableTrades;
  }, [trades, sortConfig]);

  // Total P/L is now from backend stats for accuracy across all pages
  // This is just a fallback for current page display
  const totalProfitLoss = React.useMemo(() => {
    return sortedTrades.reduce((acc, trade) => {
      return acc + (parseFloat(trade.actual_profit_loss) || 0);
    }, 0);
  }, [sortedTrades]);

  const handleSetDateFilter = (filter) => {
    setActiveDateFilter(filter);
    setCurrentPage(1); // Reset to page 1 when filter changes
    const today = new Date();
    if (filter === 'all') {
      setStartDate('');
      setEndDate('');
    } else if (filter === 'today') {
      const todayStr = today.toISOString().split('T')[0];
      setStartDate(todayStr);
      setEndDate(todayStr);
    } else if (filter === 'thisWeek') {
      const startOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
      setStartDate(startOfWeek.toISOString().split('T')[0]);
      setEndDate(new Date().toISOString().split('T')[0]);
    } else if (filter === 'last30Days') {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 29); // inclusive 30 days
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(end.toISOString().split('T')[0]);
    }
  };

  // Only show full loading screen on initial load (no trades yet)
  if (isLoading && trades.length === 0) {
    return <div className="p-6 text-center text-textPrimary bg-backgroundBody min-h-screen">Loading trades...</div>;
  }

  // If error, show error message and potentially the modal (if triggered by useEffect's catch)
  // The modal will also be shown if trades.length is 0 and user clicks "Change Account"
  // Or if user clicks "Change Account" from the main view.

  const accountSelectorForHeader = (
    <div className="flex items-center gap-2">
      <AccountSelection
        selectedAccount={account_uuid}
        setSelectedAccount={(newId) => {
          if (newId && newId !== account_uuid) {
            navigate(`/accounts/${newId}/trade-history`);
          }
        }}
        accountBalance={null} // Not displayed in compact mode
        setAccountBalance={setDummyBalance} // Dummy setter
        accountEquity={null} // Not displayed in compact mode
        setAccountEquity={setDummyEquity} // Dummy setter
        setRiskPercentFromSettings={dummySetRiskPercent} // Dummy setter
        displayMode="compact"
      />
      <button
        type="button"
        className={`px-3 py-1.5 text-sm rounded-md ${syncing ? 'bg-slate-300 dark:bg-slate-600 cursor-not-allowed' : 'bg-primary hover:bg-primaryHover text-textAccent'}`}
        disabled={syncing || !account_uuid}
        title="Trigger a manual sync of open trades on this account"
        onClick={async () => {
          if (!account_uuid) return;
          setSyncMessage(null);
          setSyncing(true);
          try {
            const resp = await syncAccountTrades(account_uuid);
            // Interceptor returns data; backend returns 202 Accepted with a message
            const msg = (resp && (resp.detail || resp.message)) || 'Sync task queued.';
            setSyncMessage({ type: 'success', text: msg });
            // Optional: refresh trades after a short delay, since sync is async
            setTimeout(() => {
              getAccountTrades(account_uuid)
                .then((data) => {
                  // API returns paginated response: { count, next, previous, results }
                  const tradesArray = data?.results || data;
                  setTrades(Array.isArray(tradesArray) ? tradesArray : []);
                  setTotalCount(data?.count || (Array.isArray(tradesArray) ? tradesArray.length : 0));
                })
                .catch((err) => {
                  console.error('Error refreshing trades after sync:', err);
                });
            }, 1500);
          } catch (err) {
            setSyncMessage({ type: 'error', text: err?.message || 'Failed to queue sync.' });
          } finally {
            setSyncing(false);
            setTimeout(() => setSyncMessage(null), 4000);
          }
        }}
      >
        {syncing ? 'Syncing…' : 'Sync'}
      </button>
    </div>
  );

  return (
    // Use PageLayout, applying consistent padding via PageLayout's own classes
    // The PageLayout itself will have p-4 md:p-6, bg-backgroundSurface, etc.
    // The div that PageLayout renders is the new "outer" container for this page's content.
    // We might need an additional wrapper if PageLayout is inside another container from App.jsx
    // For now, assume PageLayout is the primary page wrapper.
    // The min-h-screen should ideally be on a higher level container if sidebar is present.
    // For now, let PageLayout handle its own min-height if it's the main content area.
    <PageLayout
      title="Trade History"
      headerRightContent={accountSelectorForHeader}
      backButton={{ label: 'Back to History', to: '/history', preferHistory: false, hideLabelOnMobile: true }}
    >
      {/* Error display, now inside PageLayout */}
      {error && (
        <div className="p-4 mb-4 text-center text-destructive bg-red-100 border border-red-400 dark:bg-destructive/20 dark:border-destructive/50 dark:text-destructive rounded-md">
          Error: {error}
        </div>
      )}

      {syncMessage && (
        <div
          className={`p-3 mb-3 rounded-md border text-sm ${
            syncMessage.type === 'success'
              ? 'bg-green-100 border-green-300 text-green-800 dark:bg-green-900/20 dark:border-green-700 dark:text-green-300'
              : 'bg-red-100 border-red-300 text-red-800 dark:bg-red-900/20 dark:border-red-700 dark:text-red-300'
          }`}
        >
          {syncMessage.text}
        </div>
      )}

      {/* Filters and Sorting Section - At top so it applies to all elements below */}
      <div className="mb-4 sm:mb-6 p-2 sm:p-3 bg-backgroundSurface border border-borderDefault rounded-lg flex flex-col gap-3">
        {/* Date Range Picker */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-2 border border-borderDefault rounded-md p-2 bg-backgroundBody w-full sm:w-auto">
            <span className="text-lg text-textSecondary hidden sm:block">📅</span>
            <input
              type="date"
              id="dateFrom"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setActiveDateFilter(null);
              }}
              className="bg-transparent text-textPrimary text-sm focus:outline-none flex-1 sm:flex-none"
            />
            <span className="text-textSecondary">-</span>
            <input
              type="date"
              id="dateTo"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setActiveDateFilter(null);
              }}
              className="bg-transparent text-textPrimary text-sm focus:outline-none flex-1 sm:flex-none"
            />
          </div>

          {/* Preset Buttons */}
          <div className="flex flex-wrap items-center gap-1 sm:gap-2">
            <button
              onClick={() => handleSetDateFilter('all')}
              className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded-md transition-colors
                          ${activeDateFilter === 'all'
                            ? 'bg-primary text-textAccent font-semibold'
                            : 'bg-backgroundBody hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-textPrimary'}`}
            >
              All
            </button>
            <button
              onClick={() => handleSetDateFilter('today')}
              className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded-md transition-colors
                          ${activeDateFilter === 'today'
                          ? 'bg-primary text-textAccent font-semibold'
                          : 'bg-backgroundBody hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-textPrimary'}`}
          >
            Today
          </button>
          <button
            onClick={() => handleSetDateFilter('thisWeek')}
            className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded-md transition-colors
                        ${activeDateFilter === 'thisWeek'
                          ? 'bg-primary text-textAccent font-semibold'
                          : 'bg-backgroundBody hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-textPrimary'}`}
          >
            This Week
          </button>
          <button
            onClick={() => handleSetDateFilter('last30Days')}
            className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded-md transition-colors
                        ${activeDateFilter === 'last30Days'
                          ? 'bg-primary text-textAccent font-semibold'
                          : 'bg-backgroundBody hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-textPrimary'}`}
          >
            Last 30 Days
          </button>
          </div>
        </div>

        {/* Sort and Total P/L Row */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-2">
            <label htmlFor="sortBy" className="text-xs sm:text-sm text-textSecondary whitespace-nowrap">Sort by:</label>
            <select 
              id="sortBy" 
              className="block flex-1 sm:w-auto bg-backgroundBody dark:bg-slate-700 border-borderDefault text-textPrimary rounded-md shadow-sm p-1.5 sm:p-2 text-xs sm:text-sm focus:ring-primary focus:border-primary"
              value={`${sortConfig.key}-${sortConfig.direction}`}
              onChange={(e) => {
                const [key, direction] = e.target.value.split('-');
                setSortConfig({ key, direction });
              }}
            >
              <option value="created_at-descending">Newest</option>
              <option value="created_at-ascending">Oldest</option>
              <option value="instrument-ascending">Instrument (A-Z)</option>
              <option value="instrument-descending">Instrument (Z-A)</option>
              <option value="actual_profit_loss-descending">P/L (High-Low)</option>
              <option value="actual_profit_loss-ascending">P/L (Low-High)</option>
            </select>
          </div>
          {/* Total P/L Display */}
          <div className="flex items-center gap-2 p-1.5 sm:p-2 rounded-md bg-backgroundBody sm:ml-auto">
            <span className="text-xs sm:text-sm font-medium text-textSecondary">Net P/L:</span>
            <span className={`text-sm sm:text-lg font-bold ${performanceStats?.total_pnl >= 0 ? 'text-success' : performanceStats?.total_pnl < 0 ? 'text-destructive' : 'text-textSecondary'}`}>
              {performanceStats?.total_pnl?.toFixed(2) ?? '...'}
            </span>
          </div>
        </div>
      </div>

      {totalCount > pageSize && (
        <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md text-sm text-blue-800 dark:text-blue-300">
          📊 Showing page {currentPage} of {Math.ceil(totalCount / pageSize)} ({totalCount} total trades). Use pagination controls below the table.
        </div>
      )}

      <EquityCurveWidget 
        accountId={account_uuid} 
        startDate={startDate} 
        endDate={endDate} 
        trades={sortedTrades}
        equityCurveData={performanceStats?.equity_curve}
      />
      <PerformanceStats 
        trades={sortedTrades} 
        backendStats={performanceStats}
        loading={statsLoading}
      />
      
      {sortedTrades.length === 0 && !isLoading && !error && (
         <p className="p-6 text-center text-textSecondary">No trades found for this period.</p>
      )}

      {sortedTrades.length > 0 && (
        <>
          {/* Trade List - Table Layout */}
          <div className={`overflow-x-auto relative ${isLoading ? 'opacity-60' : ''}`}>
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-backgroundBody/50 z-10">
                <div className="text-sm text-textSecondary">Loading...</div>
              </div>
            )}
            <table className="w-full min-w-[1000px] text-sm">
              <thead className="bg-slate-100 dark:bg-slate-700 text-textPrimary">
                <tr>
                  <th className="p-3 text-left font-semibold whitespace-nowrap">Instrument</th>
                  <th className="p-3 text-left font-semibold whitespace-nowrap">Direction</th>
                  <th className="p-3 text-left font-semibold whitespace-nowrap">Entry Price</th>
                  <th className="p-3 text-left font-semibold whitespace-nowrap">Lot Size</th>
                  <th className="p-3 text-left font-semibold whitespace-nowrap">Status</th>
                  <th className="p-3 text-left font-semibold whitespace-nowrap">P/L</th>
                  <th className="p-3 text-left font-semibold whitespace-nowrap">DD</th>
                  <th className="p-3 text-left font-semibold whitespace-nowrap">RU</th>
                  <th className="p-3 text-left font-semibold whitespace-nowrap">Close Reason</th>
                  <th className="p-3 text-left font-semibold whitespace-nowrap">Source</th>
                  <th className="p-3 text-left font-semibold whitespace-nowrap">AI System</th>
                  <th className="p-3 text-right font-semibold whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedTrades.map((trade) => (
                  <React.Fragment key={trade.id}>
                    <tr className="bg-backgroundSurface border-b border-borderDefault hover:bg-slate-50 dark:hover:bg-slate-800">
                      <td className="p-3 font-medium whitespace-nowrap">{trade.instrument}</td>
                      <td className="p-3 whitespace-nowrap">
                        <span className={`${trade.direction === 'BUY' ? 'text-success' : 'text-destructive'} font-medium`}>{trade.direction}</span>
                      </td>
                      <td className="p-3 font-medium whitespace-nowrap">@{trade.entry_price}</td>
                      <td className="p-3 font-medium whitespace-nowrap">{trade.lot_size}</td>
                      <td className="p-3 whitespace-nowrap">
                        <span className={`font-semibold ${trade.trade_status === 'closed' ? 'text-textSecondary' : 'text-yellow-500 dark:text-yellow-400'}`}>{trade.trade_status}</span>
                      </td>
                      <td className="p-3 whitespace-nowrap">
                        <span className={`${getProfitLossColorClass(trade.actual_profit_loss)} font-medium`}>{trade.actual_profit_loss !== null ? parseFloat(trade.actual_profit_loss).toFixed(2) : 'N/A'}</span>
                      </td>
                      <td className="p-3 font-medium whitespace-nowrap">{trade.max_drawdown != null ? parseFloat(trade.max_drawdown).toFixed(2) : 'N/A'}</td>
                      <td className="p-3 font-medium whitespace-nowrap">{trade.max_runup != null ? parseFloat(trade.max_runup).toFixed(2) : 'N/A'}</td>
                      <td className="p-3 whitespace-nowrap">{trade.close_reason}</td>
                      <td className="p-3 whitespace-nowrap">{trade.source}</td>
                      <td className="p-3 whitespace-nowrap">{trade.ai_system_name || '-'}</td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => toggleTradeDetails(trade.id)}
                          title={expandedTradeId === trade.id ? 'Hide Details' : 'Show Details'}
                          className="px-3 py-1 text-sm bg-primary hover:bg-primaryHover text-textAccent rounded-md focus:outline-none"
                        >
                          {expandedTradeId === trade.id ? 'Hide' : 'Details'}
                        </button>
                      </td>
                    </tr>
                    <tr className="bg-backgroundSurface">
                      <td colSpan="12" className="px-3 pb-2">
                        <div className="text-xs text-textSecondary">
                          Created: {formatDate(trade.created_at)}
                        </div>
                        {expandedTradeId === trade.id && (
                          <div className="mt-3 border-t border-borderDefault pt-3">
                            <SpecificTradeDetails initialTradeData={trade} />
                          </div>
                        )}
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {(hasNext || hasPrevious || totalCount > pageSize) && (
            <div className="mt-6 flex items-center justify-between p-4 bg-backgroundSurface border border-borderDefault rounded-lg">
              <div className="text-sm text-textSecondary">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} trades
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={!hasPrevious}
                  className={`px-3 py-1.5 text-sm rounded-md ${
                    hasPrevious
                      ? 'bg-backgroundBody hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-textPrimary'
                      : 'bg-slate-200 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
                  }`}
                  title="First page"
                >
                  ««
                </button>
                <button
                  onClick={() => setCurrentPage(p => p - 1)}
                  disabled={!hasPrevious}
                  className={`px-3 py-1.5 text-sm rounded-md ${
                    hasPrevious
                      ? 'bg-backgroundBody hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-textPrimary'
                      : 'bg-slate-200 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
                  }`}
                  title="Previous page"
                >
                  « Previous
                </button>
                <span className="px-3 py-1.5 text-sm text-textPrimary">
                  Page {currentPage} of {Math.ceil(totalCount / pageSize)}
                </span>
                <button
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={!hasNext}
                  className={`px-3 py-1.5 text-sm rounded-md ${
                    hasNext
                      ? 'bg-backgroundBody hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-textPrimary'
                      : 'bg-slate-200 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
                  }`}
                  title="Next page"
                >
                  Next »
                </button>
                <button
                  onClick={() => setCurrentPage(Math.ceil(totalCount / pageSize))}
                  disabled={!hasNext}
                  className={`px-3 py-1.5 text-sm rounded-md ${
                    hasNext
                      ? 'bg-backgroundBody hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-textPrimary'
                      : 'bg-slate-200 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
                  }`}
                  title="Last page"
                >
                  »»
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal for account selection is now removed */}
    </PageLayout>
  );
};

export default AccountTradeListPage;
